import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GraphQLService } from './graph-ql.service';
import { Product, ProductCardType, ProductsResult, ProductThumb } from '../models';
import { catalogProductResult, productCard, productColorListOption, productDetail, productListCard } from './product-queries';
@Injectable({
  providedIn: 'root',
})
export class ProductsService extends GraphQLService {
  constructor() {
    super();
  }

  getProducts(after?: string, take = 24, searchQuery: string = '', language?: string) {
    const args: any = { first: take };
    if (after) {
      args['after'] = after;
    }

    const query = this.client.query(`@inContext(language: ${language})`, (root: any) => {
      root.addField(
        'products',
        {
          args: {
            ...args,
            query: searchQuery,
          },
        },
        (products: any) => {
          products.add('pageInfo', (pageInfo: any) => {
            pageInfo.add('hasPreviousPage');
            pageInfo.add('hasNextPage');
            pageInfo.add('endCursor');
          });
          products.add('edges', (edges: any) => {
            edges.add('cursor');

            edges.add('node', (node: any) => {
              productCard(node, this.client);
            });
          });
          products.add('filters', (filters: any) => {
            filters.add('id');
            filters.add('label');
            filters.add('type');
            filters.add('values', (values: any) => {
              values.add('id');
              values.add('label');
              values.add('count');
              values.add('input');
            });
          });
        }
      );
    });

    return from(this.client.send(query)).pipe(
      map((a: any) => {
        return a['data'].products;
      })
    ) as Observable<ProductsResult>;
  }

  getProductByHandle(handle: string, language?: string) {
    const query = this.client.query(`@inContext(language: ${language})`, (root: any) => {
      root.add('productByHandle', { args: { handle } }, (products: any) => {
        productDetail(products, this.client);
      });
    });

    return from(this.client.send(query)).pipe(
      map((a: any) => {
        return a['data'].productByHandle as Product;
      })
    ) as Observable<Product>;
  }

  // Testpagination
  getProductsOfCollection(handle: string, after: string = '', take = 24, language?: string): Observable<ProductsResult> {
    const args: any = { first: take };
    if (after) {
      args['after'] = after;
    }

    const query = this.client.query(`@inContext(language: ${language})`, (root: any) => {
      root.add('collectionByHandle', { args: { handle } }, (collection: any) => {
        collection.addConnection('products', { args }, (products: any) => {
          productCard(products, this.client);
        });
      });
    });

    return from(this.client.send(query)).pipe(
      map((a: any) => {
        return a['data'].collectionByHandle?.products as ProductsResult;
      })
    ) as Observable<ProductsResult>;
  }

  getProductsByQuery(searchQuery: string, cardType: ProductCardType = 'card', language?: string) {
    const firstCount = cardType === 'kleurmeta' ? 250 : 20;

    const query = this.client.query(`@inContext(language: ${language})`, (root: any) => {
      root.addConnection('products', { args: { first: firstCount, query: searchQuery } }, (products: any) => {
        if (cardType === 'card') {
          productCard(products, this.client);
        } else if (cardType === 'list') {
          productListCard(products, this.client);
        } else if (cardType === 'kleurmeta') {
          productColorListOption(products, this.client);
        }
      });
    });

    return from(this.client.send(query)).pipe(
      map((a: any) => {
        return a['data'].products as ProductThumb[];
      })
    ) as Observable<ProductThumb[]>;
  }

  getFilteredProducts(handle: string, filters: any, after: string = '', language?: string) {
    const args: any = { first: 24, filters };
    if (after) {
      args['after'] = after;
    }

    const query = this.client.query(`@inContext(language: ${language})`, (root: any) => {
      root.add('collectionByHandle', { args: { handle } }, (collection: any) => {
        collection.addConnection('products', { args }, (products: any) => {
          productCard(products, this.client);
        });
      });
    });

    return from(this.client.send(query)).pipe(
      map((a: any) => {
        return a['data'].collectionByHandle?.products as ProductsResult;
      })
    ) as Observable<ProductsResult>;
  }

  getRelatedProducts(productId: string, language?: string) {
    const query = this.client.query(`@inContext(language: ${language})`, (root: any) => {
      root.add('productRecommendations', { args: { productId } }, (products: any) => {
        productCard(products, this.client);
      });
    });

    return from(this.client.send(query)).pipe(
      map((a: any) => {
        return a['data'].productRecommendations as Product[];
      })
    ) as Observable<ProductThumb[]>;
  }
}
